import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSnackbar } from 'notistack';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { Empty } from 'antd';
import { getDomainMetricsApi } from "../../../api's/DashboardApi";
import LoadingIcon from '../../../reusable-component/LoadingIcon';

// const COLORS = [
//     "#4A90D9", "#5BC89A", "#4DC9BF", "#F5A623", "#F0C040",
//     "#E8734A", "#9B7FD4", "#E05C8A", "#3DB8B0", "#6C8EBF",
//     "#A3C96B", "#F28C38", "#7B68EE", "#20B2AA", "#FF6B6B",
//     "#45B7D1", "#96CEB4", "#FFEAA7", "#DDA0DD", "#98D8C8",
//     "#F7DC6F", "#BB8FCE", "#85C1E9", "#F0B27A", "#82E0AA",
//     "#F1948A", "#AED6F1", "#A9DFBF",
// ];

const COLORS = [
  '#3498db', // Flat Blue
  '#1abc9c', // Flat Turquoise
  '#2ecc71', // Flat Green
  '#f1c40f', // Flat Yellow
  '#e67e22', // Flat Orange
  '#e74c3c', // Flat Red
  '#9b59b6', // Flat Purple
  '#34495e', // Wet Asphalt
  '#16a085', // Greenish
  '#27ae60', // Emerald
  '#2980b9', // Belize Hole
  '#8e44ad', // Wisteria
  '#f39c12', // Orange
  '#d35400', // Pumpkin
  '#c0392b', // Pomegranate
  '#7f8c8d', // Concrete
  '#95a5a6', // Flat Gray
  '#bdc3c7', // Silver
];

const PAGE_SIZE = 8;
const MIN_SLICE_DEG = 8; // minimum degrees to show a label

export default function DomainOverview() {
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();

  const [domainData, setDomainData] = useState([]);

  const [hoveredIndex, setHoveredIndex] = useState(null); // index into chartData (domains with clients > 0)
  const [animated, setAnimated] = useState(false);
  const [page, setPage] = useState(0);
  const [loading, setLoading] = useState(false);
  const canvasRef = useRef(null);

  const chartData = domainData.filter((d) => d.total_clients > 0);
  const total = chartData.reduce((s, d) => s + d.total_clients, 0);

  // Create maps for chartData index and color assignment
  const chartDataIndexMap = new Map();
  chartData.forEach((d, i) => {
    chartDataIndexMap.set(d.domain_id, i);
  });

  // Create color map based on original domainData order for consistency
  const colorIndexMap = new Map();
  domainData.forEach((d, i) => {
    colorIndexMap.set(d.domain_id, i);
  });

  // Sorted descending (ALL domains, including those with 0 clients)
  // chartIndex will be null for domains with 0 clients (not in chart)
  const sorted = [...domainData]
    .map((d) => {
      const chartIndex = chartDataIndexMap.get(d.domain_id) ?? null;
      const colorIndex = colorIndexMap.get(d.domain_id) ?? 0;
      return {
        ...d,
        color: COLORS[colorIndex % COLORS.length],
        chartIndex,
      };
    })
    .sort((a, b) => b.total_clients - a.total_clients);

  const totalPages = Math.ceil(sorted.length / PAGE_SIZE);
  const pageDomains = sorted.slice(page * PAGE_SIZE, page * PAGE_SIZE + PAGE_SIZE);
  const maxClients = domainData.length > 0 ? Math.max(...domainData.map((d) => d.total_clients)) : 0;

  useEffect(() => {
    const t = setTimeout(() => setAnimated(true), 100);
    return () => clearTimeout(t);
  }, []);

  const fetchDomainData = async () => {
    setLoading(true);
    try {
      const response = await getDomainMetricsApi();
      if (response.status === 200) {
        setDomainData(response.data);
      }
    } catch (error) {
      console.error(error);
      setDomainData([]);
      enqueueSnackbar('Failed to fetch domain metrics', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDomainData();
  }, []);

  // Draw donut
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    const dpr = window.devicePixelRatio || 1;
    const size = 200;
    canvas.width = size * dpr;
    canvas.height = size * dpr;
    canvas.style.width = `${size}px`;
    canvas.style.height = `${size}px`;
    ctx.scale(dpr, dpr);

    const cx = size / 2,
      cy = size / 2;
    const outerR = 88,
      innerR = 52;
    const gap = 0.018;

    ctx.clearRect(0, 0, size, size);

    // Only draw if there's data and total > 0
    if (chartData.length === 0 || total === 0) {
      // Draw empty state - gray circle
      ctx.beginPath();
      ctx.arc(cx, cy, outerR, 0, 2 * Math.PI);
      ctx.arc(cx, cy, innerR, 0, 2 * Math.PI, true);
      ctx.closePath();
      ctx.fillStyle = '#f0f2f5';
      ctx.fill();
      return;
    }

    // Create color map based on original domainData order for consistency with legend
    const colorIndexMapForChart = new Map();
    domainData.forEach((d, i) => {
      colorIndexMapForChart.set(d.domain_id, i);
    });

    let startAngle = -Math.PI / 2;

    chartData.forEach((d, i) => {
      const sliceRad = (d.total_clients / total) * (2 * Math.PI);
      const endAngle = startAngle + sliceRad - gap;

      const isHovered = hoveredIndex === i;
      // Use the same color mapping as the legend (based on original domainData order)
      const colorIndex = colorIndexMapForChart.get(d.domain_id) ?? 0;
      const color = COLORS[colorIndex % COLORS.length];
      ctx.save();
      if (isHovered) {
        const midAngle = startAngle + sliceRad / 2;
        ctx.translate(Math.cos(midAngle) * 6, Math.sin(midAngle) * 6);
      }

      ctx.beginPath();
      ctx.arc(cx, cy, outerR, startAngle + gap / 2, endAngle);
      ctx.arc(cx, cy, innerR, endAngle, startAngle + gap / 2, true);
      ctx.closePath();
      ctx.fillStyle = color;
      ctx.globalAlpha = isHovered ? 1 : hoveredIndex !== null ? 0.65 : 1;
      ctx.fill();

      // Only draw label if slice is wide enough
      const sliceDeg = (sliceRad * 180) / Math.PI;
      if (sliceDeg >= MIN_SLICE_DEG) {
        const labelAngle = startAngle + sliceRad / 2;
        const labelR = (outerR + innerR) / 2;
        const lx = cx + Math.cos(labelAngle) * labelR;
        const ly = cy + Math.sin(labelAngle) * labelR;
        ctx.globalAlpha = 1;
        ctx.fillStyle = '#fff';
        ctx.font = `600 11px 'DM Sans', sans-serif`;
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        ctx.fillText(d.total_clients, lx, ly);
      }

      ctx.restore();
      startAngle += sliceRad;
    });
  }, [chartData, hoveredIndex, total, domainData]);

  // Hit-test
  const handleCanvasMouseMove = (e) => {
    if (chartData.length === 0 || total === 0) {
      setHoveredIndex(null);
      return;
    }
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const mx = e.clientX - rect.left - 100;
    const my = e.clientY - rect.top - 100;
    const dist = Math.sqrt(mx * mx + my * my);
    if (dist < 52 || dist > 88) {
      setHoveredIndex(null);
      return;
    }
    let angle = Math.atan2(my, mx) + Math.PI / 2;
    if (angle < 0) angle += 2 * Math.PI;
    let acc = 0;
    for (let i = 0; i < chartData.length; i++) {
      const slice = (chartData[i].total_clients / total) * (2 * Math.PI);
      if (angle >= acc && angle < acc + slice) {
        setHoveredIndex(i);
        return;
      }
      acc += slice;
    }
    setHoveredIndex(null);
  };

  const hoveredDomain = hoveredIndex !== null && hoveredIndex < chartData.length ? chartData[hoveredIndex] : null;

  return (
    <div style={s.card}>
      <h2 style={s.title}>Domain Metrics</h2>

      <div style={s.body}>
        {/* Donut */}
        <div style={s.chartWrap}>
          <div style={s.canvasWrap}>
            <canvas
              ref={canvasRef}
              onMouseMove={handleCanvasMouseMove}
              onMouseLeave={() => setHoveredIndex(null)}
              style={{
                // cursor: "pointer",
                display: 'block',
              }}
            />
            <div style={s.centerLabel}>
              {hoveredDomain ? (
                <>
                  <span style={s.centerNum}>{hoveredDomain.total_clients}</span>
                  <span style={s.centerSub} title={hoveredDomain.domain_name}>
                    {hoveredDomain.domain_name.length > 10
                      ? `${hoveredDomain.domain_name.slice(0, 9)}…`
                      : hoveredDomain.domain_name}
                  </span>
                </>
              ) : (
                <>
                  <span style={s.centerNum}>{total}</span>
                  <span style={s.centerSub}>Total Clients</span>
                </>
              )}
            </div>
          </div>
          <button style={s.viewDetails} onClick={() => navigate('/domain')}>
            View All Domains
          </button>
        </div>

        {/* Legend with carousel */}
        <div style={s.legendWrap}>
          {/* Rows */}
          <div style={s.legend}>
            {pageDomains.map((d, idx) => {
              const hasClients = d.total_clients > 0;
              const isHovered = d.chartIndex !== null && hoveredIndex === d.chartIndex;
              const barW = animated && maxClients > 0 ? (d.total_clients / maxClients) * 100 : 0;
              return (
                <div
                  key={d.domain_id}
                  style={{
                    ...s.legendRow,
                    opacity: hoveredIndex !== null && d.chartIndex !== null && !isHovered ? 0.4 : 1,
                    transition: 'opacity 0.2s',
                    // cursor: hasClients ? "pointer" : "default",
                  }}
                  onMouseEnter={() => {
                    if (d.chartIndex !== null) {
                      setHoveredIndex(d.chartIndex);
                    }
                  }}
                  onMouseLeave={() => {
                    if (d.chartIndex !== null) {
                      setHoveredIndex(null);
                    }
                  }}
                >
                  <div style={s.legendLeft}>
                    <span style={{ ...s.dot, background: d.color }} />
                    <span style={s.domainName} title={d.domain_name}>
                      {d.domain_name}
                    </span>
                  </div>
                  <div style={s.barWrap}>
                    <div
                      style={{
                        ...s.bar,
                        width: `${barW}%`,
                        background: d.color,
                        transitionDelay: `${idx * 50}ms`,
                      }}
                    />
                  </div>
                  <span style={s.countLabel}>
                    {d.total_clients} <span style={s.clientsWord}>Clients</span>
                  </span>
                </div>
              );
            })}
          </div>

          {/* Pagination controls */}
          {totalPages > 1 && (
            <div style={s.pagination}>
              <button
                style={{ ...s.pgBtn, opacity: page === 0 ? 0.35 : 1 }}
                disabled={page === 0}
                onClick={() => setPage((p) => p - 1)}
                aria-label="Previous page"
              >
                <ChevronLeftIcon style={{ fontSize: 18 }} />
              </button>

              <div style={s.dots}>
                {Array.from({ length: totalPages }).map((_, i) => (
                  <button
                    key={i}
                    onClick={() => setPage(i)}
                    style={{
                      ...s.pageDot,
                      background: i === page ? '#4A90D9' : '#d1d9e0',
                      width: i === page ? 18 : 7,
                    }}
                    aria-label={`Page ${i + 1}`}
                  />
                ))}
              </div>

              <button
                style={{ ...s.pgBtn, opacity: page === totalPages - 1 ? 0.35 : 1 }}
                disabled={page === totalPages - 1}
                onClick={() => setPage((p) => p + 1)}
                aria-label="Next page"
              >
                <ChevronRightIcon style={{ fontSize: 18 }} />
              </button>

              <span style={s.pageInfo}>
                {page * PAGE_SIZE + 1}–{Math.min((page + 1) * PAGE_SIZE, sorted.length)} of {sorted.length}
              </span>
            </div>
          )}

          {domainData.length === 0 && (
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '90%' }}>
              <Empty
                image={Empty.PRESENTED_IMAGE_SIMPLE}
                description="No domain data found"
                style={{ marginTop: 75 }}
              />
            </div>
          )}
        </div>
      </div>
      {loading && <LoadingIcon />}
    </div>
  );
}

const s = {
  card: {
    background: '#fff',
    borderRadius: 4,
    padding: '20px 28px',
    fontFamily: "'DM Sans', 'Segoe UI', sans-serif",
    boxShadow: '0 2px 16px rgba(0,0,0,0.08)',
    // maxWidth: 680,
    userSelect: 'none',
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    width: '100%',
  },
  title: {
    fontSize: 17,
    fontWeight: 700,
    color: 'var(--third-color)',
    margin: '0 0 20px',
    letterSpacing: '-0.2px',
  },
  body: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: 36,
    flex: 1,
  },
  chartWrap: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: 10,
    flexShrink: 0,
  },
  canvasWrap: {
    position: 'relative',
    width: 200,
    height: 200,
  },
  centerLabel: {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    pointerEvents: 'none',
    textAlign: 'center',
    width: 90,
  },
  centerNum: {
    fontSize: 26,
    fontWeight: 700,
    color: '#1a2233',
    lineHeight: 1,
  },
  centerSub: {
    fontSize: 10,
    color: '#8a96a8',
    marginTop: 3,
    fontWeight: 500,
    lineHeight: 1.3,
  },
  viewDetails: {
    background: 'none',
    border: 'none',
    color: '#4A90D9',
    fontSize: 13,
    fontWeight: 600,
    cursor: 'pointer',
    padding: '4px 8px',
    borderRadius: 6,
    fontFamily: 'inherit',
  },
  legendWrap: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    gap: 0,
    minWidth: 0,
  },
  legend: {
    display: 'flex',
    flexDirection: 'column',
    gap: 9,
  },
  legendRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    // cursor: "pointer",
  },
  legendLeft: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    width: 130,
    flexShrink: 0,
  },
  dot: {
    width: 9,
    height: 9,
    borderRadius: 3,
    flexShrink: 0,
  },
  domainName: {
    fontSize: 12,
    color: '#2d3a4a',
    fontWeight: 500,
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    maxWidth: 110,
  },
  barWrap: {
    flex: 1,
    height: 5,
    background: '#f0f2f5',
    borderRadius: 99,
    overflow: 'hidden',
  },
  bar: {
    height: '100%',
    borderRadius: 99,
    transition: 'width 0.7s cubic-bezier(0.4,0,0.2,1)',
  },
  countLabel: {
    fontSize: 12,
    color: '#2d3a4a',
    fontWeight: 600,
    minWidth: 62,
    textAlign: 'right',
    flexShrink: 0,
  },
  clientsWord: {
    fontWeight: 400,
    color: '#8a96a8',
  },
  pagination: {
    display: 'flex',
    alignItems: 'center',
    gap: 6,
    marginTop: 14,
    paddingTop: 10,
    borderTop: '1px solid #f0f2f5',
  },
  pgBtn: {
    background: 'none',
    border: '1px solid #d1d9e0',
    borderRadius: 6,
    width: 26,
    height: 26,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    fontSize: 16,
    color: '#4A90D9',
    fontFamily: 'inherit',
    lineHeight: 1,
    padding: 0,
    transition: 'opacity 0.15s',
    flexShrink: 0,
  },
  dots: {
    display: 'flex',
    alignItems: 'center',
    gap: 4,
    flex: 1,
  },
  pageDot: {
    height: 7,
    borderRadius: 99,
    border: 'none',
    cursor: 'pointer',
    padding: 0,
    transition: 'width 0.25s, background 0.25s',
    flexShrink: 0,
  },
  pageInfo: {
    fontSize: 11,
    color: '#8a96a8',
    whiteSpace: 'nowrap',
    flexShrink: 0,
  },
};
